/**
 * Proyecto: MOGOTAX-CONDUCTOR
 * SocketContext - Proveedor de WebSocket para actualizaciones en tiempo real
 */
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import nativeWebSocket from "./services/websocket-native";
import { useUser } from "./UserContext";
import { esAdministrador } from "./utils/perfil";

// Conductor: su id. Administrador de vehículos: sus vehículos (no tiene traslados propios).
const filtroDeUsuario = (user) =>
  esAdministrador(user)
    ? [null, (user.vehiculos || []).map((v) => v.id)]
    : [user.id, null];

export const SocketContext = createContext();

export const useSocket = () => {
  return useContext(SocketContext);
};

export const SocketProvider = ({ children }) => {
  const { user } = useUser();
  const [socketConnected, setSocketConnected] = useState(false);
  const [connectionState, setConnectionState] = useState('disconnected');

  // Conectar WebSocket cuando el usuario esté autenticado
  useEffect(() => {
    if (user && user.id) {
      const [conductorId, vehiculoIds] = filtroDeUsuario(user);
      // Al cambiar de perfil la conexión sigue abierta: solo cambia el filtro
      nativeWebSocket.setFiltro(conductorId, vehiculoIds);
      const currentState = nativeWebSocket.getConnectionState();
      if (currentState !== 'connected' && currentState !== 'connecting') {
        console.log('🔌 WebSocket conectando para usuario:', user.id);
        nativeWebSocket.connect(conductorId, vehiculoIds);
      }
    } else {
      // Desconectar si no hay usuario
      nativeWebSocket.disconnect();
    }

    // Cleanup al desmontar
    return () => {
      if (!user) {
        nativeWebSocket.disconnect();
      }
    };
  }, [user]);

  // Suscribirse a cambios de estado de conexión
  useEffect(() => {
    const unsubscribe = nativeWebSocket.onStatusChange((state) => {
      setConnectionState(state);
      setSocketConnected(state === 'connected');
    });

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, []);

  // Reconectar cuando se recupere la conexión a internet
  useEffect(() => {
    const handleOnline = () => {
      if (user && user.id) {
        const currentState = nativeWebSocket.getConnectionState();
        if (currentState !== 'connected' && currentState !== 'connecting') {
          console.log('🌐 Conexión a Internet restaurada, reconectando WebSocket...');
          setTimeout(() => {
            const state = nativeWebSocket.getConnectionState();
            if (state !== 'connected' && state !== 'connecting') {
              nativeWebSocket.connect(...filtroDeUsuario(user));
            }
          }, 2000);
        }
      }
    };

    const handleOffline = () => {
      console.log('📵 Sin conexión a Internet');
      setConnectionState('disconnected');
      setSocketConnected(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [user]);

  /**
   * Registrar listener para actualizaciones de traslado
   */
  const onTrasladoUpdate = useCallback((callback) => {
    return nativeWebSocket.onTrasladoUpdate(callback);
  }, []);

  /**
   * Registrar listener para nuevos traslados creados
   */
  const onTrasladoCreated = useCallback((callback) => {
    return nativeWebSocket.onTrasladoCreated(callback);
  }, []);

  /**
   * Registrar listener para cambios de estado
   */
  const onTrasladoStatusChange = useCallback((callback) => {
    return nativeWebSocket.onTrasladoStatusChange(callback);
  }, []);

  /**
   * Registrar listener para traslados actualizados completamente
   */
  const onTrasladoActualizado = useCallback((callback) => {
    return nativeWebSocket.onTrasladoActualizado(callback);
  }, []);

  /**
   * Registrar listener para traslados asignados al conductor
   */
  const onTrasladoAsignado = useCallback((callback) => {
    return nativeWebSocket.onTrasladoAsignado(callback);
  }, []);

  /**
   * Registrar listener para traslados cancelados
   */
  const onTrasladoCancelado = useCallback((callback) => {
    return nativeWebSocket.onTrasladoCancelado(callback);
  }, []);

  /**
   * Forzar reconexión del WebSocket
   */
  const reconnect = useCallback(() => {
    if (user && user.id) {
      nativeWebSocket.disconnect();
      setTimeout(() => {
        nativeWebSocket.connect(...filtroDeUsuario(user));
      }, 500);
    }
  }, [user]);

  const contextValue = useMemo(
    () => ({
      socket: nativeWebSocket,
      socketConnected,
      connectionState,
      onTrasladoUpdate,
      onTrasladoCreated,
      onTrasladoStatusChange,
      onTrasladoActualizado,
      onTrasladoAsignado,
      onTrasladoCancelado,
      reconnect,
    }),
    [
      socketConnected,
      connectionState,
      onTrasladoUpdate,
      onTrasladoCreated,
      onTrasladoStatusChange,
      onTrasladoActualizado,
      onTrasladoAsignado,
      onTrasladoCancelado,
      reconnect,
    ]
  );

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
};

SocketProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
